/** Server minimum (Password schema in @ana/contracts). */
export const MIN_PASSWORD_LENGTH = 12;
/** Server maximum (Password schema in @ana/contracts). */
export const MAX_PASSWORD_LENGTH = 128;

export interface PasswordCheck {
  id: 'length' | 'email' | 'repeat' | 'match';
  label: string;
  ok: boolean;
}

/**
 * The server password policy (Password schema + passwordPolicyViolation) as a checklist the person sees while
 * typing. It only guides: the server checks again and has the last word (e.g. "different from the current one").
 * Every item starts pending on an empty field, so nothing reads as satisfied before anything was typed.
 */
export function passwordChecklist(
  password: string,
  confirm: string,
  email: string,
): { items: PasswordCheck[]; valid: boolean } {
  const local = (email.split('@')[0] ?? '').toLowerCase();
  const items: PasswordCheck[] = [
    { id: 'length', label: 'Mínimo de 12 caracteres', ok: password.length >= MIN_PASSWORD_LENGTH },
  ];
  // Same threshold as the server: a short local part (e.g. "ana") would forbid too many ordinary passwords.
  if (local.length >= 4) {
    items.push({
      id: 'email',
      label: 'Não contém a parte do e-mail antes do @',
      ok: password !== '' && !password.toLowerCase().includes(local),
    });
  }
  items.push(
    {
      id: 'repeat',
      label: 'Não repete um único caractere',
      ok: password !== '' && !/^(.)\1+$/.test(password),
    },
    { id: 'match', label: 'As duas senhas são iguais', ok: confirm !== '' && confirm === password },
  );
  return { items, valid: items.every((i) => i.ok) };
}

/**
 * Token of a set-password link ("/definir-senha#token=…"). It travels in the fragment because browsers never send
 * the fragment to servers, proxies or in the Referer header. Longer than the server accepts (TokenInspectRequest)
 * means a mangled link: treated as invalid without a request.
 */
export function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  return token && token.length <= 200 ? token : null;
}
